// COMP-ORDER-EDITOR — درخواست آزمایش و تصویربرداری در مواجهه (US-MED-040، 041؛ Alt+O): نوع، اولویت، پنل یک‌کلیکی، اقلام از فهرست آزمایه‌های
// نمونه (IsSample) یا متن آزاد، اطلاعات بالینی، تشخیص، مرکز مقصد یا بخش داخلی و دستور آمادگی؛ پس از ثبت چاپ برگه با بارکد و QR.
// AP-MED-04 (US-MED-082، 083): تاریخ اجرای درخواست، دکمه «+» تکرار آزمایش با فاصله روز، و برای تصویربرداری Modifier حاجب
// و ناحیه یا سمت. الزام حاجب و ناحیه از پرچم‌های فهرست پاراکلینیک می‌آید و سرور با PHX-MED-0323 و PHX-MED-0324 آن را
// می‌سنجد، پس این فرم هر دو را برای اقلام تصویربرداری پیشنهاد می‌دهد.
import Alert from '@mui/material/Alert';
import Checkbox from '@mui/material/Checkbox';
import Chip from '@mui/material/Chip';
import FormControlLabel from '@mui/material/FormControlLabel';
import MenuItem from '@mui/material/MenuItem';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import { unwrap, type MedOrderItemRequest, type MedOrderPriority, type MedOrderType, type MedOrderView } from '@phoenix/api-client';
import { useIdempotencyKey, useTenantSession } from '@phoenix/auth';
import { useTranslation } from '@phoenix/i18n';
import { FormDialog, SampleBadge } from '@phoenix/ui';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { COMP_ORDER_EDITOR, medKeys, saveBlob } from './common';
import { EffectiveDateField, OrderItemImagingFields, OrderItemRepeatFields, TODAY_EFFECTIVE, toEffectiveRequest, useEffectiveDateOptions, type EffectiveDraft } from './highVolume';

const TYPES: readonly MedOrderType[] = ['Lab', 'Imaging', 'Other'];
const PRIORITIES: readonly MedOrderPriority[] = ['Routine', 'Urgent'];

export async function printOrder(tenantApi: ReturnType<typeof useTenantSession>['tenantApi'], order: Pick<MedOrderView, 'id' | 'number'>) {
  const response = await tenantApi.GET('/api/v1/medical-orders/orders/{id}/print', { params: { path: { id: order.id }, query: { format: 'Pdf' } }, parseAs: 'blob' });
  if (response.response.ok && response.data) {
    saveBlob(response.data as unknown as Blob, `${order.number}.pdf`);
  }
}

export function OrderEditor({ open, encounterId, patientId, onClose, onCreated }: { open: boolean; encounterId: string; patientId: string; onClose: () => void; onCreated?: (order: MedOrderView) => void }) {
  const { t } = useTranslation('med');
  const { tenantApi, session } = useTenantSession();
  const queryClient = useQueryClient();
  const key = useIdempotencyKey();
  const tenantId = session?.tenantId;
  const [type, setType] = useState<MedOrderType>('Lab');
  const [priority, setPriority] = useState<MedOrderPriority>('Routine');
  const [items, setItems] = useState<MedOrderItemRequest[]>([]);
  const [panelId, setPanelId] = useState('');
  const [query, setQuery] = useState('');
  const [clinicalInfo, setClinicalInfo] = useState('');
  const [diagnosis, setDiagnosis] = useState('');
  const [partnerId, setPartnerId] = useState('');
  const [internal, setInternal] = useState(false);
  const [preparation, setPreparation] = useState('');
  const [effective, setEffective] = useState<EffectiveDraft>(TODAY_EFFECTIVE);
  const [created, setCreated] = useState<MedOrderView | null>(null);
  const effectiveOptions = useEffectiveDateOptions(null, open);
  const repeatMax = Number(effectiveOptions.data?.orderRepeatMax ?? 10);

  const replaceItem = (next: MedOrderItemRequest) => setItems((current) => current.map((item) => (item.code === next.code ? next : item)));

  const tests = useQuery({ queryKey: medKeys.labTests(tenantId, `${type}:${query}`), enabled: open, queryFn: () => unwrap(tenantApi.GET('/api/v1/medical-orders/lab-tests', { params: { query: { q: query || undefined, type } } })) });
  const panels = useQuery({ queryKey: medKeys.panels(tenantId), enabled: open, queryFn: () => unwrap(tenantApi.GET('/api/v1/medical-orders/order-panels')) });
  const partners = useQuery({ queryKey: medKeys.partners(tenantId), enabled: open, queryFn: () => unwrap(tenantApi.GET('/api/v1/medical-orders/partners')) });
  const panel = panels.data?.items.find((item) => item.id === panelId);

  const save = useMutation({
    mutationFn: () =>
      unwrap(
        tenantApi.POST('/api/v1/medical-orders/orders', {
          body: {
            encounterId,
            type,
            priority,
            items: panel && items.length === 0 ? [] : items,
            clinicalInfo: clinicalInfo.trim() || null,
            diagnosis: diagnosis.trim() || null,
            partnerId: internal ? null : partnerId || null,
            internal,
            preparation: preparation.trim() || null,
            panelId: panelId || null,
            effective: toEffectiveRequest(effective),
          },
          headers: { 'Idempotency-Key': key.current() },
        }),
      ),
    onSettled: (_data, error) => key.settle(error ?? undefined),
    onSuccess: (order) => {
      setCreated(order);
      void queryClient.invalidateQueries({ queryKey: medKeys.patient(tenantId, patientId, 'orders') });
      onCreated?.(order);
    },
  });

  if (created) {
    return (
      <FormDialog open={open} titleKey="med:order.title" submitKey="med:order.print" screenId={COMP_ORDER_EDITOR} onSubmit={() => void printOrder(tenantApi, created)} onClose={onClose} isDirty={false} isSubmitting={false}>
        <Alert severity="success" data-testid="med-order-created">
          {t('med:order.created', { number: created.number })}
        </Alert>
      </FormDialog>
    );
  }

  return (
    <FormDialog
      open={open}
      titleKey="med:order.title"
      submitKey="med:order.submit"
      screenId={COMP_ORDER_EDITOR}
      maxWidth="md"
      onSubmit={() => (items.length > 0 || panel ? save.mutate() : undefined)}
      onClose={onClose}
      isDirty={items.length > 0}
      isSubmitting={save.isPending}
      submitError={save.error}
    >
      <Stack direction="row" spacing={1}>
        <TextField select fullWidth label={t('med:order.type')} value={type} onChange={(event) => setType(event.target.value as MedOrderType)} slotProps={{ htmlInput: { 'data-testid': 'med-order-type' } }}>
          {TYPES.map((value) => (
            <MenuItem key={value} value={value}>
              {t(`med:order_type.${value}`)}
            </MenuItem>
          ))}
        </TextField>
        <TextField select fullWidth label={t('med:order.priority')} value={priority} onChange={(event) => setPriority(event.target.value as MedOrderPriority)} slotProps={{ htmlInput: { 'data-testid': 'med-order-priority' } }}>
          {PRIORITIES.map((value) => (
            <MenuItem key={value} value={value}>
              {t(`med:priority.${value}`)}
            </MenuItem>
          ))}
        </TextField>
      </Stack>
      <TextField
        select
        label={t('med:order.panel')}
        value={panelId}
        onChange={(event) => {
          setPanelId(event.target.value);
          const next = panels.data?.items.find((item) => item.id === event.target.value);
          if (next) {
            setItems(next.items);
            setType(next.type);
            setPreparation(next.preparation ?? '');
          }
        }}
        slotProps={{ htmlInput: { 'data-testid': 'med-order-panel' } }}
      >
        <MenuItem value="">{t('med:order.no_panel')}</MenuItem>
        {(panels.data?.items ?? []).map((item) => (
          <MenuItem key={item.id} value={item.id}>
            {item.name} · {t(`med:scope.${item.scope}`)}
          </MenuItem>
        ))}
      </TextField>
      <TextField label={t('med:order.search')} value={query} onChange={(event) => setQuery(event.target.value)} slotProps={{ htmlInput: { 'data-testid': 'med-order-search' } }} />
      <Stack direction="row" spacing={0.5} useFlexGap flexWrap="wrap" data-testid="med-order-tests">
        {(tests.data?.items ?? []).slice(0, 12).map((test) => (
          <Chip
            key={test.code}
            size="small"
            variant={items.some((item) => item.code === test.code) ? 'filled' : 'outlined'}
            label={`${test.name} (${test.code})`}
            onClick={() => setItems((current) => (current.some((item) => item.code === test.code) ? current.filter((item) => item.code !== test.code) : [...current, { code: test.code, name: test.name, repeatCount: 1 }]))}
            data-testid="med-order-test"
          />
        ))}
        {tests.data?.isSample ? <SampleBadge /> : null}
      </Stack>
      <Stack direction="row" spacing={0.5} useFlexGap flexWrap="wrap" data-testid="med-order-items">
        {items.map((item) => (
          <Chip key={item.code} color="primary" label={item.name} onDelete={() => setItems((current) => current.filter((value) => value.code !== item.code))} />
        ))}
      </Stack>
      <EffectiveDateField value={effective} options={effectiveOptions.data} onChange={setEffective} testId="med-order-effective" />
      {items.length > 0 ? (
        <Stack spacing={1} data-testid="med-order-item-details">
          {items.map((item) =>
            type === 'Imaging' ? (
              <OrderItemImagingFields key={item.code} item={item} onChange={replaceItem} />
            ) : (
              <OrderItemRepeatFields key={item.code} item={item} max={repeatMax} onChange={replaceItem} />
            ),
          )}
        </Stack>
      ) : null}
      <TextField label={t('med:order.clinical_info')} value={clinicalInfo} multiline minRows={2} onChange={(event) => setClinicalInfo(event.target.value)} slotProps={{ htmlInput: { maxLength: 1000, 'data-testid': 'med-order-clinical' } }} />
      <TextField label={t('med:order.diagnosis')} value={diagnosis} onChange={(event) => setDiagnosis(event.target.value)} slotProps={{ htmlInput: { maxLength: 200 } }} />
      <FormControlLabel control={<Checkbox checked={internal} onChange={(event) => setInternal(event.target.checked)} slotProps={{ input: { 'aria-label': t('med:order.internal') } }} />} label={t('med:order.internal')} />
      {!internal && (
        <TextField select label={t('med:order.partner')} value={partnerId} onChange={(event) => setPartnerId(event.target.value)} slotProps={{ htmlInput: { 'data-testid': 'med-order-partner' } }}>
          <MenuItem value="">{t('med:order.no_partner')}</MenuItem>
          {(partners.data?.items ?? []).filter((item) => item.isActive).map((item) => (
            <MenuItem key={item.id} value={item.id}>
              {item.name}
            </MenuItem>
          ))}
        </TextField>
      )}
      <TextField label={t('med:order.preparation')} value={preparation} onChange={(event) => setPreparation(event.target.value)} slotProps={{ htmlInput: { maxLength: 500 } }} />
    </FormDialog>
  );
}
